"use client"

import { useCallback, useEffect, useState } from "react"
import { formatBRL } from "@/lib/utils"
import { BotaoDaPagina, CampoDaPagina } from "@/components/page-builder/pecas-da-pagina"
import type { PageTheme } from "@/lib/services/lead-service"

/*
  ─── O cupom, na página de venda (13/10/2026) ───

  O personal cria o cupom na área "Cupons" do produto, e ele vale de dois jeitos:

  - o link da página com o cupom (`?cupom=CODIGO`): a página confere o código na
    API e já abre com o desconto aplicado;
  - o código digitado em "Tem um cupom?", no pagamento.

  Quem confere de verdade é a API: o preço com desconto daqui é só o que a página
  mostra, e a compra manda `desconto_esperado` para parar (em vez de cobrar o
  preço cheio calada) se o cupom deixou de valer enquanto a pessoa preenchia.

  Nasceu como link de divulgação, com a venda contada para quem divulgou e o link
  guardado no aparelho por 30 dias; no mesmo dia o Leonardo pediu só o cupom.
*/

const BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || "https://api.welltrainer.com.br"

const CODIGO_MIN = 3

export type CupomNaPagina = {
  codigo: string
  situacao: "VALE" | "SEM_DESCONTO" | "VENCEU" | "ESGOTOU"
  desconto: { tipo: "PERCENTUAL" | "VALOR"; valor: number; escrito: string; nas_renovacoes: boolean } | null
  /** Por que o cupom não deu desconto: "O cupom ANA10 venceu." */
  aviso: string | null
  /** O preço de cada opção com o cupom. Nulo = o cupom não vale para aquele preço. */
  precos: Array<{ id: string; valor: number; valor_com_desconto: number | null }>
}

/** O mesmo formato da API: maiúsculas, sem acento, só letras e números. */
function normalizar(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 20)
}

/** O cupom do link da página (`?cupom=CODIGO`), já no formato da API. */
function cupomDaUrl(): string | null {
  if (typeof window === "undefined") return null
  const codigo = normalizar(new URLSearchParams(window.location.search).get("cupom") ?? "")
  return codigo.length >= CODIGO_MIN ? codigo : null
}

type Resposta = { ok: true; dados: CupomNaPagina } | { ok: false; status: number; mensagem: string | null }

async function conferir(slug: string, codigo: string): Promise<Resposta> {
  try {
    const res = await fetch(`${BASE_URL}/produto/vendas/${encodeURIComponent(slug)}/codigo/${encodeURIComponent(codigo)}`)
    const dados = await res.json().catch(() => ({}))
    if (res.ok) return { ok: true, dados }
    return { ok: false, status: res.status, mensagem: dados?.message ?? dados?.mensagem ?? null }
  } catch {
    return { ok: false, status: 0, mensagem: "Não deu para conferir o cupom agora. Confira a internet e tente de novo." }
  }
}

export function useCupomDaPagina(slug: string) {
  /* O cupom com desconto em uso, do link ou digitado. */
  const [cupom, setCupom] = useState<CupomNaPagina | null>(null)
  const [deOndeVeio, setDeOndeVeio] = useState<"link" | "digitado" | null>(null)
  /* Por que o cupom não está dando desconto, quando a pessoa precisa saber. */
  const [aviso, setAviso] = useState<string | null>(null)

  useEffect(() => {
    const codigo = cupomDaUrl()
    if (!codigo) return
    let vivo = true
    void conferir(slug, codigo).then((r) => {
      if (!vivo) return
      if (r.ok && r.dados.desconto) {
        setCupom(r.dados)
        setDeOndeVeio("link")
      } else if (r.ok) {
        setAviso(r.dados.aviso ?? `O cupom ${codigo} não está mais valendo.`)
      } else if (r.status === 404) {
        /* Apagado ou desligado depois de o link ser divulgado. */
        setAviso(`O cupom ${codigo} não está mais valendo.`)
      }
    })
    return () => {
      vivo = false
    }
  }, [slug])

  /** O cupom digitado no pagamento. Devolve a frase do problema, ou null quando aplicou. */
  const aplicar = useCallback(
    async (digitado: string): Promise<string | null> => {
      const codigo = normalizar(digitado)
      if (codigo.length < CODIGO_MIN) return "O cupom tem pelo menos 3 letras ou números."
      const r = await conferir(slug, codigo)
      if (!r.ok) return r.mensagem ?? `Não encontramos o cupom ${codigo}.`
      if (!r.dados.desconto) return r.dados.aviso ?? `O cupom ${codigo} não dá desconto.`
      setCupom(r.dados)
      setDeOndeVeio("digitado")
      setAviso(null)
      return null
    },
    [slug],
  )

  /** A compra voltou com "o cupom não vale mais": o desconto sai, e a frase fica. */
  const naoVale = useCallback((mensagem: string) => {
    setCupom(null)
    setDeOndeVeio(null)
    setAviso(mensagem)
  }, [])

  /** O preço com o cupom, ou null quando não há cupom ou ele não vale para esse preço. */
  const precoCom = (precoId: string | null | undefined): number | null => {
    if (!cupom?.desconto || !precoId) return null
    return cupom.precos.find((p) => p.id === precoId)?.valor_com_desconto ?? null
  }

  return {
    cupom,
    deOndeVeio,
    aviso,
    precoCom,
    aplicar,
    naoVale,
    /** O cupom que a compra leva: só o que dá desconto. */
    codigoParaCompra: cupom?.desconto ? cupom.codigo : null,
  }
}

// ── As peças ─────────────────────────────────────────────────────────────────

/** A faixa no topo de quem chegou pelo link com cupom: o desconto já está guardado. */
export function FaixaDoCupom({ tema, cupom }: { tema: PageTheme; cupom: CupomNaPagina }) {
  if (!cupom.desconto) return null
  return (
    <div
      className="px-4 py-2 text-center text-[0.875rem] font-medium"
      style={{ background: `${tema.primary}22`, color: tema.foreground, borderBottom: `1px solid ${tema.border}` }}
      role="status"
    >
      {`Cupom ${cupom.codigo}: ${cupom.desconto.escrito} de desconto no pagamento.`}
    </div>
  )
}

/** O preço riscado e o preço com o cupom, no cartão do preço. */
export function PrecoComCupom({
  tema,
  cheio,
  comCupom,
  cor,
  grande,
}: {
  tema: PageTheme
  cheio: number
  comCupom: number | null
  cor: string
  grande?: boolean
}) {
  if (comCupom == null) {
    return grande ? (
      <p className="text-4xl font-bold" style={{ color: cor }}>
        R$ {formatBRL(cheio)}
      </p>
    ) : (
      <span className="shrink-0 text-[1.125rem] font-bold" style={{ color: cor }}>
        R$ {formatBRL(cheio)}
      </span>
    )
  }
  return grande ? (
    <div>
      <p className="text-[0.9375rem] line-through" style={{ color: tema.muted }}>
        R$ {formatBRL(cheio)}
      </p>
      <p className="text-4xl font-bold" style={{ color: cor }}>
        R$ {formatBRL(comCupom)}
      </p>
    </div>
  ) : (
    <span className="shrink-0 text-right">
      <span className="block text-[0.8125rem] line-through" style={{ color: tema.muted }}>
        R$ {formatBRL(cheio)}
      </span>
      <span className="block text-[1.125rem] font-bold" style={{ color: cor }}>
        R$ {formatBRL(comCupom)}
      </span>
    </span>
  )
}

/**
 * O cupom no pagamento: aplicado (com "usar outro"), ou o "Tem um cupom?" que abre
 * o campo. A frase de quando o cupom não vale fica embaixo.
 */
export function CampoDoCupom({
  tema,
  cupom,
  aviso,
  precoNaoCabe,
  aoAplicar,
}: {
  tema: PageTheme
  cupom: CupomNaPagina | null
  aviso: string | null
  /** O cupom em uso não vale para o preço escolhido (ficaria abaixo do mínimo). */
  precoNaoCabe: boolean
  aoAplicar: (codigo: string) => Promise<string | null>
}) {
  const [aberto, setAberto] = useState(false)
  const [texto, setTexto] = useState("")
  const [erro, setErro] = useState<string | null>(null)
  const [conferindo, setConferindo] = useState(false)

  const aplicar = async () => {
    if (conferindo || !texto.trim()) return
    setConferindo(true)
    setErro(null)
    const problema = await aoAplicar(texto)
    setConferindo(false)
    if (problema) {
      setErro(problema)
      return
    }
    setAberto(false)
    setTexto("")
  }

  const comDesconto = !!cupom?.desconto

  return (
    <div className="space-y-2 text-left">
      {comDesconto && !aberto ? (
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <p className="min-w-0 text-[0.9375rem]" style={{ color: tema.foreground }}>
            {precoNaoCabe
              ? `O cupom ${cupom!.codigo} não vale para este preço.`
              : `Cupom ${cupom!.codigo} aplicado: ${cupom!.desconto!.escrito} de desconto.`}
          </p>
          <button
            type="button"
            className="min-h-[44px] text-[0.875rem] underline underline-offset-2"
            style={{ color: tema.muted }}
            onClick={() => setAberto(true)}
          >
            Usar outro cupom
          </button>
        </div>
      ) : !aberto ? (
        <button
          type="button"
          className="min-h-[44px] text-[0.9375rem] underline underline-offset-2"
          style={{ color: tema.foreground }}
          onClick={() => setAberto(true)}
        >
          Tem um cupom?
        </button>
      ) : (
        <div className="space-y-2">
          <CampoDaPagina
            theme={tema}
            id="checkout-cupom"
            rotulo="Cupom"
            placeholder="ANA10"
            valor={texto}
            onChange={(v) => {
              setTexto(v.toUpperCase().slice(0, 30))
              setErro(null)
            }}
          />
          <div className="flex gap-2">
            <BotaoDaPagina theme={tema} tom="contorno" carregando={conferindo} desativado={!texto.trim()} onClick={() => void aplicar()}>
              Aplicar
            </BotaoDaPagina>
            <BotaoDaPagina
              theme={tema}
              tom="contorno"
              onClick={() => {
                setAberto(false)
                setTexto("")
                setErro(null)
              }}
            >
              Cancelar
            </BotaoDaPagina>
          </div>
          {erro && (
            <p className="text-[0.8125rem]" style={{ color: "#DE2828" }} role="alert">
              {erro}
            </p>
          )}
        </div>
      )}
      {aviso && !comDesconto && (
        <p className="text-[0.8125rem]" style={{ color: tema.muted }} role="status">
          {aviso}
        </p>
      )}
    </div>
  )
}
